import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { ExternalLink, DollarSign, Building2, ChevronDown, ChevronRight } from 'lucide-react';
import { getIncome } from '@/lib/api';
import { useNavigate } from 'react-router-dom';

const MONTH_NAMES = ['', 'January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

function formatCurrency(val) {
  if (val === null || val === undefined) return '\u2014';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(val);
}

export function IncomeBreakdownPopup({ open, onClose, currentIncome, monthLabel }) {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expandedProps, setExpandedProps] = useState({});

  const now = new Date();
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth() + 1;

  useEffect(() => {
    if (open) {
      fetchIncome();
    }
  }, [open]);

  // Auto-expand all properties so units are visible by default — what the user expects.
  useEffect(() => {
    if (data?.properties?.length) {
      const next = {};
      data.properties.forEach(p => { next[p.property_id] = true; });
      setExpandedProps(next);
    }
  }, [data]);

  const fetchIncome = async () => {
    setLoading(true);
    try {
      const result = await getIncome(currentYear);
      const monthData = result.months?.find(m => m.month === currentMonth);
      setData(monthData);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  const toggleProp = (id) => {
    setExpandedProps(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const handleGoToRentRoll = () => {
    onClose();
    navigate('/rent-roll');
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-hidden flex flex-col" data-testid="income-breakdown-popup">
        <DialogHeader>
          <DialogTitle className="text-lg font-heading flex items-center gap-2">
            <DollarSign className="h-4 w-4" />
            {monthLabel || `${MONTH_NAMES[currentMonth]} ${currentYear}`} Income
          </DialogTitle>
          <DialogDescription>Breakdown by property and unit</DialogDescription>
        </DialogHeader>

        <ScrollArea className="flex-1 -mx-6 px-6">
          <div className="space-y-4 pb-4">
            {/* Total */}
            <div className="text-center py-3">
              <p className="text-3xl font-heading font-bold tabular-nums" data-testid="income-popup-total">
                {formatCurrency(currentIncome)}
              </p>
              <p className="text-xs text-muted-foreground mt-1">Total income this month</p>
            </div>

            <Separator />

            {loading && <p className="text-sm text-muted-foreground text-center py-4">Loading breakdown...</p>}

            {!loading && data && data.properties && (
              <div className="space-y-2">
                {data.properties.map(prop => (
                  <div key={prop.property_id} className="border rounded-lg overflow-hidden">
                    <button
                      className="w-full flex items-center justify-between px-3 py-2 hover:bg-muted/30 transition-colors duration-150"
                      onClick={() => toggleProp(prop.property_id)}
                      data-testid={`income-prop-${prop.property_id}`}
                    >
                      <div className="flex items-center gap-2">
                        <Building2 className="h-3.5 w-3.5 text-muted-foreground" />
                        <span className="text-sm font-medium">{prop.property_name}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-semibold tabular-nums">{formatCurrency(prop.total)}</span>
                        {expandedProps[prop.property_id] ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
                      </div>
                    </button>
                    {expandedProps[prop.property_id] && prop.units && (
                      <div className="border-t">
                        <Table>
                          <TableBody>
                            {prop.units.map(unit => (
                              <TableRow key={unit.unit_id} className="hover:bg-muted/20">
                                <TableCell className="text-xs py-1.5 px-3">Unit {unit.unit_number}</TableCell>
                                <TableCell className="text-xs py-1.5 tabular-nums text-right px-3">
                                  {formatCurrency(unit.total)}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            {!loading && (!data || !data.properties || data.properties.length === 0) && (
              <p className="text-sm text-muted-foreground text-center py-4">No income data available for this month.</p>
            )}
          </div>
        </ScrollArea>

        <Separator />
        <DialogFooter>
          <Button variant="outline" size="sm" onClick={handleGoToRentRoll} className="gap-1.5" data-testid="income-popup-go-to-page">
            <ExternalLink className="h-3.5 w-3.5" /> Go to Rent Roll
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
